import * as React from 'react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.21. Shimmer is a translating gradient rather than a pulsing
// opacity so it reads as "loading" instead of "disabled"; it collapses to a
// static tint under prefers-reduced-motion via the motion-reduce variant.
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-sm bg-surface-subtle',
        'before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer',
        'before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent',
        'motion-reduce:before:hidden',
        className,
      )}
      {...props}
    />
  );
}

/** Paragraph placeholder. Last line is 60% width so it reads as prose (§2.21). */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3', index === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full')}
        />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ className }: { className?: string }) {
  return <Skeleton className={cn('h-9 w-9 shrink-0 rounded-full', className)} />;
}

/**
 * Mirrors the 72px queue row (§4.3) so content swap causes no layout shift.
 * `aria-busy` + `aria-hidden` keep it out of the accessibility tree while the
 * live region announces the eventual result count.
 */
export function SkeletonRow({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-hidden="true" className="divide-y divide-line">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex h-18 items-center gap-4 px-4">
          <SkeletonAvatar />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
          <Skeleton className="h-[22px] w-16 rounded-full" />
          <Skeleton className="h-3 w-20" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      aria-busy="true"
      aria-hidden="true"
      className={cn('rounded-md border border-line bg-white p-5 shadow-card', className)}
    >
      <Skeleton className="h-2.5 w-24" />
      <Skeleton className="mt-3 h-7 w-16" />
      <Skeleton className="mt-3 h-3 w-32" />
    </div>
  );
}
